<template>
  <div class="quotation-options-modal-body">
    <v-toolbar class="modal-toolbar" density="comfortable" flat>
      <div class="modal-bar">
        <v-btn icon variant="text" class="back-btn" aria-label="Back" @click="modal.close()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="modal-title">Create Quotation</div>
      </div>
    </v-toolbar>

    <v-card-text class="body py-6">
      <div class="options-container">
        <div class="option-card" @click="handleCreateNew">
          <div class="option-icon">
            <v-icon size="24">mdi-plus-circle</v-icon>
          </div>
          <div class="option-content">
            <h3 class="option-title">Create New</h3>
            <p class="option-description">Create a custom quotation from scratch</p>
          </div>
          <v-icon size="20" class="option-chevron">mdi-chevron-right</v-icon>
        </div>

        <div class="templates-section">
          <h3 class="section-title">Available Templates</h3>
          <div class="templates-list">
            <AppCard 
              v-for="template in availableTemplates" 
              :key="template.id" 
              class="template-item"
              @click="handleSelectTemplate(template)"
            >
                <div class="template-info">
                <div class="template-header">
                  <v-icon size="16" class="template-icon-small">mdi-file-document</v-icon>
                  <div class="template-text">
                    <span class="template-name">{{ template.name }}</span>
                    <span v-if="template.subtitle" class="template-subtitle">{{ template.subtitle }}</span>
                  </div>
                </div>
              </div>
            </AppCard>
          </div>
        </div>
      </div>
    </v-card-text>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useModalStore } from "@/shared/stores/modal";
import { useRouter } from "vue-router";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import { getQuotationTemplateList } from "@/modules/vendor/services/quotation-templates.api";

type QuotationTemplate = {
  id: string | number;
  name: string;
  subtitle?: string;
};

const props = defineProps<{
  inquiryId: string | number;
}>();

const modal = useModalStore();
const router = useRouter();
const snackbar = useSnackbarStore();

const availableTemplates = ref<QuotationTemplate[]>([]);
const isLoading = ref(false);

async function loadTemplates() {
  if (isLoading.value) return;
  isLoading.value = true;

  try {
    const response = await getQuotationTemplateList();
    const resolved = response?.data ?? response ?? [];
    const rawTemplates = Array.isArray(resolved) ? resolved : resolved?.templates ?? [];

    availableTemplates.value = (rawTemplates as any[]).map((template) => ({
      id: template.id,
      name: template.name ?? template.title ?? "",
      subtitle: template.subtitle ?? template.description ?? "",
    }));
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to load templates.";
    snackbar.error(message);
  } finally {
    isLoading.value = false;
  }
}

onMounted(loadTemplates);

function handleCreateNew() {
  modal.close();
  router.push({
    name: "vendor.quotation.form",
  });
}

function handleSelectTemplate(template: QuotationTemplate) {
  modal.close();
  router.push({
    name: "vendor.quotation.template.detail",
    params: {
      id: template.id,
    },
    query: {
      inquiryId: String(props.inquiryId ?? ""),
    },
  });
}

</script>

<style scoped>
.quotation-options-modal-body {
  padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 16px);
  min-height: 100dvh;
}

.modal-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-theme);
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 0 0;
  height: auto;
}

.modal-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
}

:deep(.modal-toolbar .v-toolbar__content) {
  height: auto;
  min-height: 64px;
  align-items: center;
  padding: 8px 16px;
}

.back-btn {
  width: 50px;
  height: 50px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.body {
  padding-top: 12px;
}

.options-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.option-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.option-card:hover {
  background: #f8fafc;
  border-color: var(--color-theme);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.option-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  color: var(--color-theme);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.option-content {
  flex: 1;
  min-width: 0;
}

.option-title {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}

.option-description {
  margin: 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.4;
}

.option-chevron {
  color: #94a3b8;
  flex-shrink: 0;
}

.templates-section {
  margin-top: 20px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.templates-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.template-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: var(--radius-lg);
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.template-item:hover {
  border-color: var(--color-theme);
  background: #fff;
  transform: translateY(-1px);
}

.template-info {
  flex: 1;
  min-width: 0;
}

.template-header {
  gap: 8px;
}

.template-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.template-name {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}

.template-subtitle {
  font-size: 14px;
  color: #64748b;
  margin-top: 2px;
}

.template-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.template-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.view-btn,
.select-btn {
  color: var(--color-theme);
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.select-btn {
  background: rgba(232, 97, 47, 0.1);
  color: #fff;
}


.template-chevron-small {
  color: #94a3b8;
}
</style>
